import type { GeneratedQuestion } from '../generators/math/types';

export interface BankJson {
  kp: string;
  textbook: string;
  grade?: number;
  semester?: number;
  questions: (GeneratedQuestion & { surfaceVariant: string })[];
}

const cache = new Map<string, BankJson>();

export async function loadBank(url: string): Promise<BankJson> {
  if (cache.has(url)) return cache.get(url)!;
  const resp = await fetch(url);
  if (!resp.ok) throw new Error(`Failed to load bank ${url}: ${resp.status}`);
  const bank = (await resp.json()) as BankJson;
  cache.set(url, bank);
  return bank;
}

// ─── Option C 救火: seeded sampling ───────────────────────────────────────────
//
// Why: 既有 sampleBank 用 Math.random — 不可重玩同一份卷, 不可測試, kid 投訴
// 「換湯不換藥」也沒辦法精準復現。Option C spec §S2 要求 sampleBank 可選 seed,
// 若 seed 給定則 deterministic, 否則保留原本「每次不同」行為以免 regress
// 既有 _generic-bank.ts 呼叫端。
//
// 演算法: mulberry32(seedHash) Fisher-Yates shuffle, surfaceVariant dedup
// 邏輯不動 (避免破壞既有測試與 6875 題的 variant uniqueness 假設)。

/** Mulberry32 PRNG — 32-bit deterministic, matches mock-paper-composer.ts. */
function mulberry32(seed: number): () => number {
  let s = seed | 0;
  return () => {
    s = (s + 0x6d2b79f5) | 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Hash a string seed to int32. 與 rebalance-correct-answer.cjs 不同算法
 *  (那邊用 sha256 因為要 deterministic 跨 runtime),這裡走純 JS hash
 *  避免引入 crypto 依賴(browser bundle size)。 */
function hashSeedString(s: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/** Normalise seed: number passes through, string is hashed, undefined → null
 *  meaning "non-seeded, use Math.random". */
function resolveSeed(seed?: number | string): number | null {
  if (seed === undefined || seed === null) return null;
  if (typeof seed === 'number') return seed | 0;
  return hashSeedString(seed) | 0;
}

/** Fisher-Yates shuffle. randFn returns [0,1). */
function shuffleInPlace<T>(arr: T[], randFn: () => number): T[] {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(randFn() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

/**
 * Sample N questions ensuring no two share the same surfaceVariant.
 * Falls back to allowing duplicates only if not enough distinct variants.
 *
 * @param bank  loaded BankJson
 * @param n     desired sample size
 * @param seed  OPTIONAL — number or string. When provided, output is fully
 *              deterministic (same bank + n + seed ⇒ same questions in same
 *              order). When omitted, falls back to Math.random (legacy
 *              behaviour, preserves _generic-bank.ts "every lesson different"
 *              feel where seed not yet wired up).
 */
export function sampleBank(
  bank: BankJson,
  n: number,
  seed?: number | string,
): GeneratedQuestion[] {
  const resolved = resolveSeed(seed);
  const randFn = resolved === null
    ? Math.random
    : mulberry32(resolved);

  const shuffled = shuffleInPlace([...bank.questions], randFn);

  const used = new Set<string>();
  const out: GeneratedQuestion[] = [];
  for (const q of shuffled) {
    if (out.length >= n) break;
    if (used.has(q.surfaceVariant)) continue;
    used.add(q.surfaceVariant);
    out.push(q);
  }
  // If we couldn't fill n with unique variants, fill from remaining
  if (out.length < n) {
    for (const q of shuffled) {
      if (out.length >= n) break;
      if (!out.includes(q)) out.push(q);
    }
  }
  return out;
}

// ─── Fix 3 helpers exported for overlay / lesson runner ──────────────────────

/** Public PRNG factory for callers wiring per-question seeds (overlay shuffle). */
export function makeSeededRng(seed: number | string): () => number {
  return mulberry32(resolveSeed(seed) ?? 0);
}

/** Hash exported so callers can derive child seeds from string identifiers. */
export function hashSeed(s: string): number {
  return hashSeedString(s);
}
